import React from 'react';
import { Box, type BoxProps } from '@chakra-ui/react';

export interface ProseProps extends BoxProps {}

const headingBase = {
  fontFamily: 'var(--oci-fonts-heading)',
  fontWeight: 700,
  fontVariationSettings: '"wdth" 72, "wght" 760',
  color: 'inherit',
  marginBottom: '1rem',
  '&:not(:first-of-type)': {
    marginTop: '3rem'
  }
};

const proseStyles = {
  '& a': { color: 'var(--oci-colors-oci-secondary)' },

  '& ul': { paddingLeft: 6, listStyleType: 'disc' },
  '& ol': { paddingLeft: 6, listStyleType: 'decimal' },

  '& sup, & sub': {
    fontSize: '0.75em'
  },
  '& sup': {
    verticalAlign: 'middle'
  },
  '& sub': {
    verticalAlign: 'bottom'
  },

  '& h1, & h2, & h3, & h4, & h5, & h6': headingBase,

  '& h1': { fontSize: '2.25rem' },
  '& h2': { fontSize: '2rem' },
  '& h3': { fontSize: '1.75rem' },
  '& h4': { fontSize: '1.5rem' },
  '& h5': { fontSize: '1.25rem' },
  '& h6': { fontSize: '1rem' },

  '& dl': { padding: 0 },

  '& ol ol, & ol ul, & ul ol, & ul ul': {
    marginBottom: 0
  },

  '& dt': {
    fontWeight: 700
  },

  '& figure > figcaption': {
    fontSize: '0.75rem',
    lineHeight: '1rem',
    color: 'oci.base.400',
    padding: '1rem',
    textAlign: 'center',
    width: '100%',
    maxWidth: '52rem',
    margin: '0 auto'
  },

  '& figure img': {
    display: 'block',
    margin: '0 auto'
  },

  '& > *': {
    marginBottom: '1.5rem'
  },

  '& > *:last-child': {
    marginBottom: 0
  },

  '& table': {
    width: '100%',
    maxWidth: '100%',
    borderColor: 'var(--oci-colors-oci-base-200a)',
    borderCollapse: 'collapse',

    '& tbody, & td, & tfoot, & th, & thead, & tr': {
      borderColor: 'inherit',
      borderBottomColor: 'inherit',
      borderStyle: 'solid',
      borderWidth: 0,
      borderBottomWidth: 0
    },

    '& > *:not(caption) > * > *': {
      borderBottomWidth: '1px'
    },

    '& > :not(:last-child) > :last-child > *': {
      borderBottomWidth: '2px'
    },

    '& th': {
      fontFamily: 'var(--oci-fonts-heading)',
      fontWeight: 700,
      fontVariationSettings: '"wdth" 72, "wght" 760',
      fontSize: '1rem'
    },

    '& td, & th': {
      padding: '0.25rem',
      verticalAlign: 'top',
      textAlign: 'left'
    },

    '& th:first-of-type, & td:first-of-type': {
      paddingLeft: '1rem'
    },

    '& th:last-child, & td:last-child': {
      paddingRight: '1rem'
    },

    '&.table-zebra tbody tr:nth-of-type(odd)': {
      background: 'var(--oci-colors-oci-base-50a)'
    },

    '& caption': {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      color: 'oci.base.400',
      captionSide: 'bottom',
      padding: '1rem',
      textAlign: 'center'
    }
  }
};

/**
 * OCI Prose — Chakra replacement for devseed-ui Prose.
 *
 * Provides rich-text formatting for child elements (h1-h6, tables, lists, etc.).
 * Use `styled(Prose)` to extend — works with styled-components via forwardRef.
 */
export const Prose = React.forwardRef<HTMLDivElement, ProseProps>(
  function Prose(props, ref) {
    return <Box ref={ref} className='oci-prose' css={proseStyles} {...props} />;
  }
);
